/* eslint-disable no-lonely-if */
import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { canNavigate } from '@/libs/acl/routeProtection'

import driverRoutes from '../views/driver/routes'
import servicesRoutes from '../views/services/routes'
import zonesRoutes from '../views/zones/routes'
import usersRoutes from '../views/users/routes'
import billingRoutes from '../views/billing/routes'
import tripsRoutes from '../views/trips/routes'
import announcementRoutes from '../views/marketing/announcements/routes'
import adsRoutes from '../views/marketing/advertisements/routes'
import tripConfigRoutes from '../views/trip-configurations/routes'
import promoCodesRoutes from '../views/marketing/promo-codes/routes'
import fraudTripsRoutes from '../views/fraud-trips/routes'
import notificationsRoutes from '../views/marketing/notifications/routes'
import groupsRoutes from '../views/marketing/groups/routes'
import logsRoutes from '../views/logs/router'
import dispatchConfigRoutes from '../views/dispatch-configurations/routes'
import pricingOverrideRoutes from '../views/billing/pricing-override/routes'
import rewardsRoutes from '../views/marketing/rewards/routes'
import businessesRoutes from '../views/businesses/routes'
import teamsRoutes from '../views/teams/routes'
import subscriptionPlansRoutes from '../views/subscription-plans/routes'
import extraPaymentDiscountRoutes from '../views/marketing/extra-payment-discount/routes'
import learningCenterQuizRoutes from '../views/learning-center-quiz/routes'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/home/pages/Home.vue'),
      meta: {
        requiresAuth: true,
        pageTitle: 'Home',
        breadcrumb: [
          {
            text: 'Home',
            active: true,
          },
        ],
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/dashboard/Dashboard.vue'),
      meta: {
        requiresAuth: true,
        pageTitle: 'Dashboard',
        resource: 'dashboard',
        action: 'read',
        breadcrumb: [
          {
            text: 'Dashboard',
            active: true,
          },
        ],
        allowedCountries: ['LY'],
      },
    },
    {
      path: '/jobs',
      name: 'jobs',
      component: () => import('@/views/jobs/jobs.vue'),
      meta: {
        requiresAuth: true,
        pageTitle: 'Jobs',
        resource: 'jobs',
        action: 'read',
        breadcrumb: [
          {
            text: 'Jobs',
            active: true,
          },
        ],
      },
    },
    {
      path: '/supply-and-demand',
      name: 'supply-and-demand',
      component: () => import('@/views/suppy-and-demand/index.vue'),
      meta: {
        requiresAuth: true,
        pageTitle: 'Supply & Demand',
        resource: 'supply-and-demand',
        action: 'read',
        breadcrumb: [
          {
            text: 'Supply & Demand',
            active: true,
          },
        ],
      },
    },
    {
      path: '/comp-eta',
      name: 'comp-eta',
      component: () => import('@/views/competitors-eta/competitors-eta.vue'),
      meta: {
        requiresAuth: true,
        pageTitle: 'Competitors ETA',
        breadcrumb: [
          {
            text: 'Competitors ETA',
            active: true,
          },
        ],
      },
    },
    {
      path: '/configured-comp-eta',
      name: 'configured-comp-eta',
      component: () => import('@/views/competitors-eta/configured-competitors-list.vue'),
      meta: {
        requiresAuth: true,
        pageTitle: 'Configured Competitors ETA',
        breadcrumb: [
          {
            text: 'Configured Competitors ETA',
            active: true,
          },
        ],
      },
    },
    ...driverRoutes,
    ...servicesRoutes,
    ...zonesRoutes,
    ...usersRoutes,
    ...billingRoutes,
    ...tripsRoutes,
    ...tripConfigRoutes,
    ...promoCodesRoutes,
    ...fraudTripsRoutes,
    ...notificationsRoutes,
    ...groupsRoutes,
    ...logsRoutes,
    ...dispatchConfigRoutes,
    ...pricingOverrideRoutes,
    ...rewardsRoutes,
    ...businessesRoutes,
    ...announcementRoutes,
    ...adsRoutes,
    ...teamsRoutes,
    ...subscriptionPlansRoutes,
    ...extraPaymentDiscountRoutes,
    ...learningCenterQuizRoutes,
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: {
        guest: true,
        layout: 'full',
      },
    },
    {
      path: '/new-password',
      name: 'new-password',
      component: () => import('@/views/NewPassword.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/error/Error404.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '*',
      redirect: 'error-404',
    },
    {
      path: '/not-authorized',
      name: 'misc-not-authorized',
      component: () => import('@/views/pages/miscellaneous/NotAuthorized.vue'),
      meta: {
        layout: 'full',
      },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  if (!store.getters['auth/isAuthenticated']) {
    await store.dispatch('auth/currentSession')
  }
  next()
})

router.beforeEach(async (to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (store.getters['auth/isAuthenticated']) {
      next()
      return
    }
    localStorage.setItem('cachedURL', to.fullPath)
    next('/login')
  } else {
    next()
  }
})

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.guest)) {
    if (store.getters['auth/isAuthenticated']) {
      next('/')
      return
    }
    next()
  } else {
    next()
  }
})

router.beforeEach((to, _, next) => {
  const userData = JSON.parse(localStorage.getItem('userData'))
  const userCountry = userData?.country || 'JO'
  // const userCountry = 'LY'

  if (to.meta.resource) {
    if (!canNavigate(to)) {
      next('/not-authorized')
    } else {
      if (userCountry === 'JO') {
        next()
      } else if (userCountry !== 'JO') {
        if (!to.meta.allowedCountries) {
          next('/not-authorized')
        } else {
          next()
        }
      }
    }
  } else {
    next()
  }
})
// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
